import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  Typography,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useState } from 'react';
import FormattedAlias from '../Translations/FormattedAlias';

interface CustomRangeDialogProps {
  open: boolean;
  onClose: () => void;
  onApply: (from: Dayjs, to: Dayjs) => void;
  initialFrom?: Dayjs;
  initialTo?: Dayjs;
}

const CustomRangeDialog: React.FC<CustomRangeDialogProps> = React.memo(
  ({ open, onClose, onApply, initialFrom, initialTo }) => {
    const [startDate, setStartDate] = useState<Dayjs>(
      initialFrom ?? dayjs().subtract(24, 'hour'),
    );
    const [endDate, setEndDate] = useState<Dayjs>(initialTo ?? dayjs());

    useEffect(() => {
      if (open && initialFrom) {
        setStartDate(initialFrom);
      }
      if (open && initialTo) {
        setEndDate(initialTo);
      }
    }, [open, initialFrom, initialTo]);

    const handleApply = (): void => {
      onApply(startDate, endDate);
    };

    const handleStartChange = (value: Dayjs | null): void => {
      if (value) {
        setStartDate(value);
      }
    };

    const handleEndChange = (value: Dayjs | null): void => {
      if (value) {
        setEndDate(value);
      }
    };

    const isValidRange = startDate.isBefore(endDate);

    return (
      <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
        <DialogTitle>
          <FormattedAlias id={'Custom Time Range'} />
        </DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid2 container spacing={3} sx={{ mt: 1 }}>
              <Grid2 size={{ xs: 6 }}>
                <Typography align='center' variant='h6' gutterBottom>
                  <FormattedAlias id={'Start'} />
                </Typography>
                <DateTimePicker
                  value={startDate}
                  onChange={handleStartChange}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </Grid2>
              <Grid2 size={{ xs: 6 }}>
                <Typography align='center' variant='h6' gutterBottom>
                  <FormattedAlias id={'End'} />
                </Typography>
                <DateTimePicker
                  value={endDate}
                  onChange={handleEndChange}
                  maxDateTime={dayjs()}
                  minDateTime={startDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </Grid2>
            </Grid2>
          </LocalizationProvider>
          {!isValidRange && (
            <Typography
              color='error'
              variant='body2'
              sx={{ mt: 2, textAlign: 'center' }}
            >
              <FormattedAlias id={'Start date must be before end date'} />
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>
            <FormattedAlias id={'Cancel'} />
          </Button>
          <Button
            onClick={handleApply}
            variant='contained'
            disabled={!isValidRange}
          >
            <FormattedAlias id={'Apply Time Range'} />
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

CustomRangeDialog.displayName = 'CustomRangeDialog';

export default CustomRangeDialog;
